/**
 * ExecutorOnboardingPage — "Before you start" (s231 EXECUTOR-ONBOARDING, D-230.69 SPEC, D-231.20).
 *
 * Built from the board, not a screenshot (G4.2): docs/living/planner-magic-canvas/Home.dc.html, the
 * executor tab's `onStart` block (markup ~L240–L400) and its logic (`obQsDef`, `obTitle`, `obNoText`,
 * `obNextPerson`, `obRows`, ~L2176–L2410). Shown once after the death is verified and before Today;
 * afterwards this page reopens as a summary with a Change on each answer.
 *
 * TWO pages (D-231.43, amends D-230.69): (1) will you act; (2) "A few things we need to know" — the two
 * death questions and the relationship card together, one Save and continue.
 *
 * Reade's rulings that differ from the board as drawn:
 *   • ruling 6 — no payment step: "Yes, I will act" goes straight on. The board's "Not drawn yet"
 *     payment line is not built.
 *   • ruling 7 — the No path NAMES the backup and promises nothing ("is asked next" is dropped:
 *     nothing asks the backup yet, BACKUP-ACTIVATION-1).
 *   • ruling 8 + D-231.41(2) — once any task is done (the accept-role tick aside), the summary shows
 *     question 1 as "Yes" with no Change, and the step-back line under it.
 */
import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { AckDialog } from "@/components/executor/task-drawer/AckDialog";
import { DRAFT_ACK_ADVISORY_BY_FORM } from "@shared/constants/gov-form-framing";
import { CLAY, FONT, SAGE, WARM } from "@shared/brand";

export type DeathAnswer = "yes" | "no" | "not_sure";

export interface OnboardingState {
  plannerName: string;
  acceptedAt: string | null;
  actConfirmedAt: string | null;
  diedInCanada: DeathAnswer | null;
  workRelated: DeathAnswer | null;
  done: boolean;
  /** planValue = the planner's answer; yourValue = the executor's own typed answer (ruling 4 item 2). */
  relationship: { available: boolean; planValue: string | null; yourValue: string | null; executorName: string | null };
  backupName: string | null;
}

export interface DeclineResult {
  declined: true;
  plannerName: string;
  backupName: string | null;
  willLocation: string | null;
}

export const onboardingQueryKey = (clientId: string) => ["/api/executor", clientId, "onboarding"];

/** The onboarding state. Shared by the shell (the landing gate), the sidebar tag and this page. */
export function useExecutorOnboarding(clientId: string, enabled = true) {
  return useQuery<OnboardingState>({
    queryKey: onboardingQueryKey(clientId),
    queryFn: async () => {
      const res = await fetch(`/api/executor/${clientId}/onboarding`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: onboarding`);
      return res.json();
    },
    enabled: enabled && !!clientId,
    retry: false,
  });
}

const LABEL: Record<DeathAnswer, string> = { yes: "Yes", no: "No", not_sure: "Not sure" };
const ANSWERS: DeathAnswer[] = ["yes", "no", "not_sure"];
const ADDED_NOT_SURE = "Added to your list, marked “Check this”. You can take it off once you know.";

/** Board `obQsDef`, with the planner's real name. */
function questions(name: string) {
  return [
    {
      key: "diedInCanada" as const,
      q: "Did the death happen in Canada?",
      why: `If not, the list adds bringing ${name} home.`,
      eff: {
        yes: "No change to your list.",
        no: "Added to your list: “Arrange repatriation if death occurred outside Canada.”",
        not_sure: ADDED_NOT_SURE,
      } as Record<DeathAnswer, string>,
    },
    {
      key: "workRelated" as const,
      q: `Was the death related to ${name}’s work?`,
      why: "If yes, the list adds telling the WSIB (the Workplace Safety and Insurance Board), which may pay survivor benefits.",
      eff: {
        yes: "Added to your list: “Notify WSIB if death was work-related.”",
        no: "No change to your list.",
        not_sure: ADDED_NOT_SURE,
      } as Record<DeathAnswer, string>,
    },
  ];
}

function monthYear(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-CA", { month: "long", year: "numeric", timeZone: "UTC" });
}

const card = { border: `1px solid ${WARM.stone}`, backgroundColor: WARM.white } as const;
const warnCard = { border: `2px solid ${CLAY[800]}`, backgroundColor: CLAY[100] } as const;

function PrimaryButton({ children, onClick, disabled, testId, tone = "sage" }: {
  children: React.ReactNode; onClick: () => void; disabled?: boolean; testId: string; tone?: "sage" | "clay";
}) {
  const bg = tone === "clay" ? CLAY[800] : disabled ? SAGE[200] : SAGE[600];
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className="rounded-lg px-7 min-h-[48px] text-[15px] whitespace-nowrap"
      style={{ backgroundColor: bg, color: WARM.white, cursor: disabled ? "default" : "pointer" }}>
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled, testId }: {
  children: React.ReactNode; onClick: () => void; disabled?: boolean; testId: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className="rounded-lg px-7 min-h-[48px] text-[15px] whitespace-nowrap"
      style={{ border: `1.5px solid ${SAGE[600]}`, backgroundColor: WARM.white, color: SAGE[800] }}>
      {children}
    </button>
  );
}

/** The end card after a confirmed No. Rendered by the shell on its own: the plan is closed now. */
export function DeclinedEndCard({ clientId, result }: { clientId: string; result: DeclineResult }) {
  const name = result.plannerName;
  const [ackOpen, setAckOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The same Form 74G draft the accept-role task offers, behind the same acknowledgement (D-148.7).
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/executor/${clientId}/onboarding/form-74g`, { credentials: "include" });
      if (!res.ok) throw new Error("We couldn't prepare the form. Please try again.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "form-74g-renunciation-draft.docx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setAckOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't prepare the form. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 w-full max-w-[780px]" data-testid="onboarding-declined">
      <div className="flex flex-col gap-2.5">
        <span className="text-[13px]" style={{ color: WARM.muted }}>Before you start</span>
        <h1 className="m-0 text-[34px] leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 400, color: WARM.charcoal }}>
          You said you can&rsquo;t act
        </h1>
      </div>
      <div className="rounded-xl px-6 py-5 flex flex-col gap-3" style={card}>
        <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }} data-testid="onboarding-declined-next">
          {name}&rsquo;s plan is now closed to you.{" "}
          {result.backupName
            ? `${result.backupName} is the backup executor ${name} named.`
            : `${name} did not name a backup executor, so nobody else can open it here.`}
        </p>
        <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>
          To make it official, sign a Renunciation (Form 74G). An estate lawyer files it with the court. Until then, do not
          start any estate paperwork: once you do, stepping back needs the court&rsquo;s approval.
        </p>
        <button type="button" onClick={() => setAckOpen(true)} data-testid="onboarding-download-74g"
          className="self-start flex items-center gap-2 min-h-[44px] px-4 rounded-lg text-[14.5px] whitespace-nowrap"
          style={{ border: `1.5px solid ${SAGE[600]}`, backgroundColor: WARM.white, color: SAGE[800] }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
          </svg>
          <span>Download Form 74G, with {name}&rsquo;s name filled in</span>
        </button>
        {!result.backupName && (
          <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: WARM.cream }} data-testid="onboarding-declined-no-backup">
            <span className="text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>
              Tell whoever takes over to look for a printed copy of {name}&rsquo;s plan. {name} may have kept one with the will.
            </span>
            {result.willLocation && (
              <span className="text-[14px]" style={{ color: WARM.muted }}>
                Where the will is, from {name}&rsquo;s plan: {result.willLocation}
              </span>
            )}
          </div>
        )}
      </div>
      <AckDialog open={ackOpen} busy={busy} error={ackOpen ? error : null}
        advisory={DRAFT_ACK_ADVISORY_BY_FORM["74G"] ?? null}
        onConfirm={() => { void download(); }} onCancel={() => { setError(null); setAckOpen(false); }} />
    </div>
  );
}

/** D-231.41(2): the step-back line under question 1's "Yes" once the executor has started acting. */
/** s231 ruling 4 (D-231.57): always under question 1's answer — ticking a task here proves nothing about
 *  what the executor did in real life, so question 1 always keeps its Change; this line is the advice. */
export const STEP_BACK_ADVICE = "If you’ve already started dealing with the estate, talk to an estate lawyer before stepping back.";

export default function ExecutorOnboardingPage({ clientId, state, onDeclined }: {
  clientId: string;
  state: OnboardingState;
  onDeclined: (r: DeclineResult) => void;
}) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const name = state.plannerName;
  const hasRel = state.relationship.available;
  // D-231.43: two pages — will you act; then the two death questions and the relationship together.
  const total = 2;

  const [editing, setEditing] = useState(false);
  const [step, setStep] = useState<1 | 2>(state.actConfirmedAt ? 2 : 1);
  const [noOpen, setNoOpen] = useState(false);
  const [answers, setAnswers] = useState<{ diedInCanada: DeathAnswer | null; workRelated: DeathAnswer | null }>({
    diedInCanada: state.diedInCanada, workRelated: state.workRelated,
  });
  const relBlank = !state.relationship.planValue;
  // The executor's own earlier answer comes back as THEIRS (typed into the box), never as the plan's.
  const yourValue = state.relationship.yourValue;
  const initialRelMode = (): "ok" | "fix" | null => (yourValue && !relBlank ? "fix" : null);
  const [relMode, setRelMode] = useState<"ok" | "fix" | null>(initialRelMode);
  const [relText, setRelText] = useState(yourValue ?? "");
  const [corrected, setCorrected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summary = state.done && !editing;

  // One lock for every write on this page: the steps are sequential, never concurrent.
  const flight = useSingleFlight();
  const refresh = (next: OnboardingState) => {
    queryClient.setQueryData(onboardingQueryKey(clientId), next);
    // The answers switch tasks on and off; every read keyed on this estate's task paths (the list,
    // and Today's summary of it) refetches in the background (not awaited).
    void queryClient.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith(`/api/executor/${clientId}/tasks`),
    });
  };
  const actMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/executor/${clientId}/onboarding/act`, {})).json() as Promise<OnboardingState>,
  });
  const answersMutation = useMutation({
    mutationFn: async (body: { diedInCanada: DeathAnswer; workRelated: DeathAnswer; relationship?: string }) => {
      const next = (await apiRequest("POST", `/api/executor/${clientId}/onboarding/answers`, {
        diedInCanada: body.diedInCanada, workRelated: body.workRelated,
      })).json() as Promise<OnboardingState>;
      if (!body.relationship) return next;
      await next;
      return (await apiRequest("POST", `/api/executor/${clientId}/onboarding/relationship`, { relationship: body.relationship })).json() as Promise<OnboardingState>;
    },
  });
  const declineMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/executor/${clientId}/onboarding/decline`, {})).json() as Promise<DeclineResult>,
  });

  // Every write goes through flight.run at its call site (the lock); this only surfaces a failure.
  const settle = async <T,>(pending: Promise<T | undefined>): Promise<T | undefined> => {
    setError(null);
    try {
      return await pending;
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "Something went wrong. Please try again.");
      return undefined;
    }
  };

  // Board `obFinish`: done → Today.
  const finish = () => {
    setEditing(false);
    setLocation(`/executor/${clientId}/dashboard`);
  };

  const onYes = async () => {
    const next = await settle(flight.run(() => actMutation.mutateAsync()));
    if (!next) return;
    refresh(next);
    setNoOpen(false);
    if (editing) { setEditing(false); return; } // a Change on question 1 goes back to the summary
    setStep(2);
  };
  const onConfirmNo = async () => {
    const r = await settle(flight.run(() => declineMutation.mutateAsync()));
    if (r) onDeclined(r);
  };

  const relTyping = relBlank || relMode === "fix";
  const relReady = !hasRel || (relTyping ? relText.trim().length > 0 : relMode === "ok");
  const page2Ready = !!answers.diedInCanada && !!answers.workRelated && relReady;
  const onSavePage2 = async () => {
    if (!page2Ready || !answers.diedInCanada || !answers.workRelated) return;
    const entered = hasRel && relTyping ? relText.trim() : "";
    // Re-saving the answer already on record writes no second change.
    const typed = entered && entered !== (corrected ?? yourValue ?? "") ? entered : "";
    const body = { diedInCanada: answers.diedInCanada, workRelated: answers.workRelated, ...(typed ? { relationship: typed } : {}) };
    const next = await settle(flight.run(() => answersMutation.mutateAsync(body)));
    if (!next) return;
    refresh(next);
    if (typed) setCorrected(typed);
    finish();
  };

  const title = summary ? "Before you start"
    : step === 1 ? `Are you still able to act as ${name}’s executor?`
    : "A few things we need to know";
  const accepted = monthYear(state.acceptedAt);
  const qs = questions(name);
  const noText = state.backupName
    ? `Nothing is final yet. If you confirm, ${name}’s plan closes to you. ${state.backupName} is the backup executor ${name} named.`
    : `Nothing is final yet. ${name} did not name a backup executor, so if you confirm, ${name}’s plan closes. Only people ${name} chose can open it, so nobody else can open it here.`;
  const mine = corrected ?? yourValue;
  const openChange = (to: 1 | 2) => {
    setEditing(true); setStep(to); setNoOpen(false);
    setRelMode(mine && !relBlank ? "fix" : null); setRelText(mine ?? "");
  };

  const rows: { q: string; a: string; note?: string; step: 1 | 2 | null }[] = [
    // Ruling 4 (D-231.57): question 1 ALWAYS keeps its Change, with the advice line under the answer.
    { q: `Still able to act as ${name}’s executor?`, a: "Yes", note: STEP_BACK_ADVICE, step: 1 },
    { q: "Did the death happen in Canada?", a: state.diedInCanada ? LABEL[state.diedInCanada] : "No answer yet", step: 2 },
    { q: `Was the death related to ${name}’s work?`, a: state.workRelated ? LABEL[state.workRelated] : "No answer yet", step: 2 },
    ...(hasRel ? [{
      q: `How you are related to ${name}`,
      a: mine
        ? (state.relationship.planValue ? `${mine} (your correction; ${name}’s plan says ${state.relationship.planValue})` : `${mine} (your answer)`)
        : state.relationship.planValue ? `${state.relationship.planValue}, as in ${name}’s plan` : "No answer yet",
      step: 2 as const,
    }] : []),
  ];

  return (
    <div className="flex justify-center px-4 sm:px-9 py-7">
      <div className="flex flex-col gap-5 w-full max-w-[780px]" data-testid="executor-onboarding">
        <div className="flex flex-col gap-2.5">
          <span className="text-[13px]" style={{ color: WARM.muted }}>Before you start</span>
          <h1 className="m-0 text-[28px] sm:text-[34px] leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 400, color: WARM.charcoal }}
            data-testid="onboarding-title">{title}</h1>
          {!summary && (
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                {Array.from({ length: total }, (_, i) => (
                  <div key={i} className="w-14 h-1.5 rounded-full" style={{ backgroundColor: i < step ? SAGE[600] : WARM.stoneLight }} />
                ))}
              </div>
              <span className="text-[13px] whitespace-nowrap" style={{ color: WARM.muted }} data-testid="onboarding-progress">Question {step} of {total}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg px-4 py-3 text-[14px]" style={warnCard} role="alert" data-testid="onboarding-error">{error}</div>
        )}

        {!summary && step === 1 && (
          <>
            <div className="rounded-xl px-6 py-5 flex flex-col gap-3" style={card}>
              <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>
                {accepted
                  ? `You agreed to be ${name}’s executor in ${accepted}. Now that the time has come, we ask again.`
                  : `You were named as ${name}’s executor. Now that the time has come, we ask you.`}{" "}
                Acting means you take charge of settling the estate: you work through the task list, deal with the banks and
                the government, and sign the court forms.
              </p>
              <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>
                You do not have to do it alone. You can hire a lawyer at any time, and you can talk to one before you answer.
              </p>
            </div>
            {!noOpen && (
              <div className="flex flex-wrap items-center gap-3">
                <PrimaryButton onClick={() => { void onYes(); }} disabled={flight.inFlight} testId="onboarding-yes">Yes, I will act</PrimaryButton>
                <SecondaryButton onClick={() => setNoOpen(true)} testId="onboarding-no">No, I can&rsquo;t act</SecondaryButton>
              </div>
            )}
            {noOpen && (
              <div className="rounded-xl px-5 py-4 flex flex-col gap-2.5" style={warnCard} data-testid="onboarding-no-panel">
                <span className="text-[19px]" style={{ fontFamily: FONT.serif, color: CLAY[800] }}>You said you can&rsquo;t act.</span>
                <span className="text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>{noText}</span>
                <div className="flex flex-wrap items-center gap-3">
                  <PrimaryButton tone="clay" onClick={() => { void onConfirmNo(); }} disabled={flight.inFlight} testId="onboarding-confirm-no">
                    Confirm, I can&rsquo;t act
                  </PrimaryButton>
                  <SecondaryButton onClick={() => setNoOpen(false)} testId="onboarding-no-back">Go back, I will act</SecondaryButton>
                </div>
              </div>
            )}
            {editing && (
              <button type="button" className="self-start text-[14px] min-h-[44px]" style={{ color: WARM.muted }}
                onClick={() => { setEditing(false); setNoOpen(false); }} data-testid="onboarding-cancel-change">
                &lsaquo; Back to your answers
              </button>
            )}
          </>
        )}

        {!summary && step === 2 && (
          <>
            <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>
              Your answers add tasks to your list or leave them off. We never ask how it happened.
            </p>
            {qs.map((q) => {
              const v = answers[q.key];
              const added = v ? q.eff[v].startsWith("Added") : false;
              return (
                <div key={q.key} className="rounded-xl px-6 py-5 flex flex-col gap-3" style={card} data-testid={`onboarding-q-${q.key}`}>
                  <span className="text-[20px]" style={{ fontFamily: FONT.serif, color: WARM.charcoal }}>{q.q}</span>
                  <span className="text-[14px] leading-relaxed" style={{ color: WARM.muted }}>{q.why}</span>
                  <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={q.q}>
                    {ANSWERS.map((a) => {
                      const on = v === a;
                      return (
                        <button key={a} type="button" role="radio" aria-checked={on}
                          onClick={() => setAnswers((s) => ({ ...s, [q.key]: a }))}
                          data-testid={`onboarding-${q.key}-${a}`}
                          className="min-h-[44px] px-5 rounded-full text-[14.5px] whitespace-nowrap"
                          style={{ border: `1.5px solid ${on ? SAGE[600] : WARM.stone}`, backgroundColor: on ? SAGE[50] : WARM.white, color: on ? SAGE[800] : WARM.charcoal }}>
                          {LABEL[a]}
                        </button>
                      );
                    })}
                  </div>
                  {v && (
                    <div className="px-3.5 py-2.5 rounded-lg text-[14px] leading-relaxed" style={{ backgroundColor: added ? CLAY[100] : SAGE[50], color: WARM.charcoal }}
                      data-testid={`onboarding-effect-${q.key}`}>
                      {q.eff[v]}
                    </div>
                  )}
                </div>
              );
            })}

            {hasRel && (
              <div className="rounded-xl px-6 py-5 flex flex-col gap-3" style={card} data-testid="onboarding-rel-card">
                <span className="text-[20px]" style={{ fontFamily: FONT.serif, color: WARM.charcoal }}>How you are related to {name}</span>
                <span className="text-[14px] leading-relaxed" style={{ color: WARM.muted }}>Court forms and letters all use this one answer.</span>
                <span className="text-[13px] uppercase tracking-[0.08em]" style={{ color: WARM.muted }}>
                  {relBlank ? `Not in ${name}’s plan` : `From ${name}’s plan`}
                </span>
                {!relBlank && (
                  <div className="flex items-baseline gap-3.5 flex-wrap">
                    <span className="text-[24px]" style={{ fontFamily: FONT.serif, color: WARM.charcoal, textDecoration: relMode === "fix" ? "line-through" : "none" }}
                      data-testid="onboarding-rel-plan">{state.relationship.planValue}</span>
                    {state.relationship.executorName && (
                      <span className="text-[14px]" style={{ color: WARM.muted }}>{state.relationship.executorName}, {name}&rsquo;s executor</span>
                    )}
                  </div>
                )}
                {relTyping && (
                  <>
                    <label className="text-[15px] leading-relaxed" style={{ color: WARM.charcoal }} htmlFor="onboarding-rel-input">
                      {relBlank ? `${name} did not say how you are related. In your own words:` : "Your correction, in your own words:"}
                    </label>
                    <input id="onboarding-rel-input" value={relText} onChange={(e) => setRelText(e.target.value)} maxLength={200}
                      placeholder="For example: common-law partner, daughter, friend" data-testid="onboarding-rel-input"
                      className="min-h-[46px] px-3.5 rounded-lg text-[15px] outline-none"
                      style={{ border: `1.5px solid ${relText ? SAGE[600] : WARM.stone}`, backgroundColor: WARM.white, color: WARM.charcoal }} />
                  </>
                )}
                {!relBlank && relMode !== "fix" && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => setRelMode("ok")} data-testid="onboarding-rel-ok"
                      className="rounded-lg px-7 min-h-[48px] text-[15px] whitespace-nowrap"
                      style={{ border: `1.5px solid ${SAGE[600]}`, backgroundColor: relMode === "ok" ? SAGE[600] : WARM.white, color: relMode === "ok" ? WARM.white : SAGE[800] }}>
                      That&rsquo;s right
                    </button>
                    <button type="button" onClick={() => setRelMode("fix")} className="text-[14px] min-h-[44px] px-2" style={{ color: SAGE[600] }}
                      data-testid="onboarding-rel-fix">Add a correction</button>
                  </div>
                )}
                {!relBlank && relMode === "fix" && (
                  <>
                    <button type="button" onClick={() => { setRelMode("ok"); setRelText(""); }} className="self-start text-[14px] min-h-[44px]"
                      style={{ color: WARM.muted }} data-testid="onboarding-rel-unfix">Never mind, it was right</button>
                    <span className="text-[13px] leading-relaxed" style={{ color: WARM.muted }}>
                      {name}&rsquo;s answer stays as {name} gave it, in the frozen copy. The forms and letters use yours, and the change is
                      listed in Copies and changes.
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-5">
              <PrimaryButton onClick={() => { void onSavePage2(); }} disabled={!page2Ready || flight.inFlight} testId="onboarding-save-answers">
                Save and continue
              </PrimaryButton>
              {!editing && (
                <button type="button" className="text-[14px] min-h-[44px]" style={{ color: WARM.muted }} onClick={() => setStep(1)}
                  data-testid="onboarding-back">&lsaquo; Back</button>
              )}
              {!page2Ready && (
                <span className="text-[13px]" style={{ color: WARM.muted }}>
                  {!answers.diedInCanada || !answers.workRelated ? "Answer all three to go on"
                    : relTyping ? "Type your answer to go on" : "Confirm or correct to go on"}
                </span>
              )}
            </div>
          </>
        )}

        {summary && (
          <>
            <p className="m-0 text-[15px] leading-relaxed" style={{ color: WARM.charcoal }}>Check your answers. Changing one changes your task list.</p>
            <div className="rounded-xl px-6 py-1.5" style={card} data-testid="onboarding-summary">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-4 min-h-[60px] py-2" style={{ borderBottom: `1px solid ${WARM.stoneLight}` }}
                  data-testid={`onboarding-row-${i}`}>
                  <div className="flex flex-col gap-0.5 flex-grow">
                    <span className="text-[13px]" style={{ color: WARM.muted }}>{r.q}</span>
                    <span className="text-[15px]" style={{ color: WARM.charcoal }}>{r.a}</span>
                    {r.note && (
                      <span className="text-[13px] leading-relaxed" style={{ color: WARM.muted }} data-testid="onboarding-step-back-note">{r.note}</span>
                    )}
                  </div>
                  {r.step !== null && (
                    <button type="button" className="text-[14px] min-h-[44px] whitespace-nowrap" style={{ color: SAGE[600] }}
                      onClick={() => openChange(r.step as 1 | 2)} data-testid={`onboarding-change-${i}`}>Change</button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
